/* eslint-disable */
import axios, { AxiosError, AxiosResponse } from 'axios';
import { Commit } from 'vuex';
import { ArtistTrackModel } from '@/models';

const appName = 'robopolmusic';
const base = 'https://api.audius.co/v1/';
const recentLimit = Number(process.env.VUE_APP_AUDIUS_RECENT_LIMIT || 500);
const radioAttempts = Number(process.env.VUE_APP_AUDIUS_RADIO_ATTEMPTS || 4);
const offsetWindow = Number(process.env.VUE_APP_AUDIUS_OFFSET_WINDOW || 800);
const genreOffsetWindow = Number(process.env.VUE_APP_AUDIUS_GENRE_OFFSET_WINDOW || 250);

let audiusCallCount = 0;
const recentTrackIds: string[] = [];
const recentTrackIdSet = new Set<string>();

const state = {
  tracks: []
}

const audiusGenreMap: { [key: string]: string } = {
  acoustic: 'Acoustic',
  ambient: 'Ambient',
  blues: 'Blues',
  classical: 'Classical',
  dance: 'Electronic',
  dark: 'Ambient',
  electronic: 'Electronic',
  guitar: 'Acoustic',
  hiphop: 'Hip-Hop/Rap',
  house: 'Electronic',
  jazz: 'Jazz',
  piano: 'Classical',
  pop: 'Pop',
  rap: 'Hip-Hop/Rap',
  reggae: 'Reggae',
  rnb: 'R&B/Soul',
  rock: 'Rock',
}

const mutations = {
  setTracks(state: any, payload: any[]) {
    state.tracks = payload
  },
}

const getters = {
  getTracks(state: any) {
    return state.tracks
  }
}

function mapAudiusTracks(data: any[]): ArtistTrackModel[] {
  return data.map((t: any) => ({
    id: t.id,
    name: t.title,
    artist_id: t.user ? t.user.id : 'unknown',
    artist_name: t.user ? t.user.name : 'Unknown Artist',
    album_id: '',
    album_image: '',
    album_name: '',
    audio: `https://api.audius.co/v1/tracks/${t.id}/stream?app_name=${appName}`,
    audiodownload: `https://api.audius.co/v1/tracks/${t.id}/stream?app_name=${appName}`,
    audiodownload_allowed: false,
    duration: t.duration ? t.duration.toString() : '0',
    image: t.artwork ? (t.artwork['480x480'] || t.artwork['150x150'] || '') : '',
    joindate: '',
    website: '',
    license_ccurl: '',
    releasedate: t.release_date || '',
    tracks: []
  } as ArtistTrackModel));
}

function uniqueTracks(tracks: ArtistTrackModel[]) {
  const seen = new Set<string>();
  const unique: ArtistTrackModel[] = [];

  tracks.forEach((track) => {
    if (!track.id || seen.has(track.id)) {
      return;
    }
    seen.add(track.id);
    unique.push(track);
  });

  return unique;
}

function normalizeGenreKey(value?: string) {
  return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function getAudiusGenre(genre?: string) {
  const key = normalizeGenreKey(genre);
  if (!key) {
    return '';
  }

  return audiusGenreMap[key] || genre!.charAt(0).toUpperCase() + genre!.slice(1);
}

function filterDataByGenre(data: any[], genre?: string) {
  const targetGenre = getAudiusGenre(genre);
  if (!targetGenre) {
    return data;
  }

  return data.filter((track) => normalizeGenreKey(track.genre) === normalizeGenreKey(targetGenre));
}

function createOffset(limit: number, attempt: number, explicitOffset?: number, hasGenre = false) {
  if (explicitOffset !== undefined && attempt === 0) {
    return explicitOffset;
  }

  const window = hasGenre ? genreOffsetWindow : offsetWindow;
  const maxPages = Math.max(1, Math.floor(window / limit));
  const randomPage = Math.floor(Math.random() * maxPages);
  return ((randomPage + audiusCallCount + attempt) % maxPages) * limit;
}

function createRadioUrl(payload: { limit: number; genre?: string; offset?: number }, limit: number, attempt: number) {
  const audiusGenre = getAudiusGenre(payload.genre);
  const timeRanges = audiusGenre ? ['week', 'month', 'allTime'] : ['week', 'month', 'year', 'allTime'];
  const timeRange = timeRanges[(audiusCallCount + attempt) % timeRanges.length];
  const useUnderground = (audiusCallCount + attempt) % 3 === 2;
  const endpoint = useUnderground ? 'tracks/trending/underground' : 'tracks/trending';
  const offset = createOffset(limit, attempt, payload.offset, Boolean(audiusGenre));

  let url = base + endpoint + '?app_name=' + appName + '&limit=' + limit + '&offset=' + offset + '&time=' + timeRange;
  if (audiusGenre) {
    url += '&genre=' + encodeURIComponent(audiusGenre);
  }

  return url;
}

function filterRecentTracks(tracks: ArtistTrackModel[], excludeRecent?: boolean) {
  if (!excludeRecent) {
    return tracks;
  }

  const freshTracks = tracks.filter((track) => !recentTrackIdSet.has(track.id));
  return freshTracks.length ? freshTracks : tracks;
}

function rememberTracks(tracks: ArtistTrackModel[]) {
  tracks.forEach((track) => {
    if (!track.id || recentTrackIdSet.has(track.id)) {
      return;
    }
    recentTrackIds.push(track.id);
    recentTrackIdSet.add(track.id);
  });

  while (recentTrackIds.length > recentLimit) {
    const oldTrackId = recentTrackIds.shift();
    if (oldTrackId) {
      recentTrackIdSet.delete(oldTrackId);
    }
  }
}

function fetchFallbackTracks(limit: number, excludeRecent?: boolean, remember?: boolean, genre?: string) {
  const audiusGenre = getAudiusGenre(genre);
  const genreParam = audiusGenre ? '&genre=' + encodeURIComponent(audiusGenre) : '';

  return axios.get(base + 'tracks/trending?app_name=' + appName + '&limit=' + limit + '&time=allTime' + genreParam).then((response: AxiosResponse) => {
    const data = filterDataByGenre(response.data.data || [], genre);
    let tracks = uniqueTracks(mapAudiusTracks(data));
    tracks = filterRecentTracks(tracks, excludeRecent).slice(0, limit);
    if (remember) {
      rememberTracks(tracks);
    }
    return tracks;
  });
}

const actions = {
  fetchRadioTracks(_: {commit: Commit}, payload: { limit: number; genre?: string; offset?: number; remember?: boolean; excludeRecent?: boolean }) {
    const targetLimit = Math.min(payload.limit || 50, 100);
    const requestLimit = Math.min(50, Math.max(20, targetLimit));
    const attempts = Math.max(1, Math.ceil(targetLimit / requestLimit), payload.remember ? radioAttempts : 1);
    const urls = Array.from({ length: attempts }).map((_, index) => createRadioUrl(payload, requestLimit, index));

    audiusCallCount += attempts;

    return new Promise ((resolve, reject) => {
      Promise.all(urls.map((url) => axios.get(url).then((response: AxiosResponse) => response.data.data || []).catch(() => [])))
        .then((batches: any[][]) => {
          const rawData = filterDataByGenre(([] as any[]).concat(...batches), payload.genre);
          let tracks = uniqueTracks(mapAudiusTracks(rawData));
          tracks = filterRecentTracks(tracks, payload.excludeRecent).slice(0, targetLimit);

          if (tracks.length) {
            if (payload.remember) {
              rememberTracks(tracks);
            }
            resolve(tracks);
            return;
          }

          fetchFallbackTracks(requestLimit, payload.excludeRecent, payload.remember, payload.genre)
            .then(resolve)
            .catch(reject);
        }).catch((error: AxiosError) => {
          fetchFallbackTracks(requestLimit, payload.excludeRecent, payload.remember, payload.genre)
            .then(resolve)
            .catch((e: AxiosError) => reject(Math.random() > 0.5 ? error : e));
        })
    })
  },

  searchTracks(_: {commit: Commit}, query: string) {
    const url = base + 'tracks/search?app_name=' + appName + '&query=' + encodeURIComponent(query) + '&limit=30';
    return new Promise((resolve, reject) => {
      axios.get(url).then((response: AxiosResponse) => {
        resolve(mapAudiusTracks(response.data.data || []));
      }).catch(() => resolve([]))
    })
  },

  searchUsers(_: {commit: Commit}, query: string) {
    const url = base + 'users/search?app_name=' + appName + '&query=' + encodeURIComponent(query) + '&limit=20';
    return new Promise((resolve, reject) => {
      axios.get(url).then((response: AxiosResponse) => {
        const users = (response.data.data || []).map((u: any) => ({
          id: u.id,
          name: u.name,
          image: u.profile_picture ? (u.profile_picture['480x480'] || u.profile_picture['150x150'] || '') : '',
          joindate: '',
          website: ''
        }));
        resolve(users);
      }).catch(() => resolve([]))
    })
  }
}

const audiusModule = {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}

export default audiusModule;
